import React, { useEffect, useState, useCallback } from "react";
import { IoCloseOutline } from "react-icons/io5";
import api from "../../utilis/api";
import "../../Dashboard/pages/pages.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const Bookings = () => {
  const [bookedSlots, setBookedSlots] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentSlot, setCurrentSlot] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Reusable Date Formatter (Same as Upcoming component)
  const formatBookingDate = (isoDate) => {
    if (!isoDate) return "N/A";
    const date = new Date(isoDate);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Fetch Teacher Details
  const getTeacher = useCallback(async () => {
    if (!user?._id) return;
    try {
      const res = await api.get(`teacher/${user._id}`);
      if (res.data?.data) {
        setTeacher(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch teacher:", err);
    }
  }, [user?._id]);

  // Fetch Bookings for Teacher
  const fetchBookedSlots = useCallback(async () => {
    if (!teacher?._id) return;
    setLoading(true);
    try {
      const res = await api.get("/booking", {
        params: { teacherId: teacher._id },
      });
      if (res.data?.success) {
        setBookedSlots(res.data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
    } finally {
      setLoading(false);
    }
  }, [teacher?._id]);

  useEffect(() => {
    getTeacher();
  }, [getTeacher]);

  useEffect(() => {
    fetchBookedSlots();
  }, [fetchBookedSlots]);

  // Time Formatting
  const formatHour = (h) => {
    if (!h || typeof h !== "string") return "";
    const [hour, minute] = h.trim().split(":").map(Number);
    if (isNaN(hour)) return h;
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${h12}:${(minute || 0).toString().padStart(2, "0")} ${ampm}`;
  };

  const formatSlotTime = (time, duration) => {
    if (!time || typeof time !== "string") return "N/A";

    if (time.includes("-")) {
      const [start, end] = time.split("-").map((t) => t.trim());
      return `${formatHour(start)} - ${formatHour(end)}`;
    }

    if (duration) {
      const [h, m] = time.split(":").map(Number);
      const totalMin = h * 60 + m + parseInt(duration, 10);
      const endH = String(Math.floor(totalMin / 60)).padStart(2, "0");
      const endM = String(totalMin % 60).padStart(2, "0");
      return `${formatHour(time)} - ${formatHour(`${endH}:${endM}`)}`;
    }

    return formatHour(time);
  };

  // Same timezone-safe expiry check
  const isSlotExpired = (slot) => {
    try {
      const now = new Date();
      const timeStr = slot.time?.includes("-")
        ? slot.time.split("-")[0].trim()
        : slot.time?.trim();

      if (!timeStr) return false;

      const [hourStr, minStr] = timeStr.split(":");
      const startHour = parseInt(hourStr, 10);
      const startMinute = parseInt(minStr, 10) || 0;

      const rawDate = new Date(slot.date);
      const slotDate = new Date(
        rawDate.getUTCFullYear(),
        rawDate.getUTCMonth(),
        rawDate.getUTCDate(),
        startHour,
        startMinute,
        0,
        0,
      );

      return now >= slotDate;
    } catch (e) {
      console.warn("Error in isSlotExpired:", e);
      return false;
    }
  };

  const openSlotModal = (slot) => {
    setCurrentSlot(slot);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setCurrentSlot(null);
  };
  const updateStatus = async (slot) => {
    try {
      if (!slot?._id) {
        return toast.error("Slot is not available");
      }
      console.log(slot);
      const res = await api.patch(`/booking/complete/${slot._id}`);
      console.log(res);
      if (res.data?.success) {
        toast.success(res.data.message || "Booking marked as completed");
      } else {
        toast.error(res.data?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Update Status Error:", error);

      const message =
        error.response?.data?.message || "Something went wrong while updating";

      toast.error(message);
    }
  };
  return (
    <div className="overview-container">
      <h2>My Bookings</h2>

      {loading ? (
        <p>Loading bookings...</p>
      ) : bookedSlots.length > 0 ? (
        <div className="table-responsive">
          {/* Desktop Table */}
          <table className="lessons-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Time</th>
                <th>Duration</th>
                <th>Student Name</th>
                <th>Student Email</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {bookedSlots.map((slot, idx) => {
                const expired = isSlotExpired(slot);
                return (
                  <tr key={slot._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{formatBookingDate(slot.date)}</td> {/* ← Fixed Here */}
                    <td>{formatSlotTime(slot.time, slot.duration)}</td>
                    <td>{slot.duration ? `${slot.duration} min` : "N/A"}</td>
                    <td>{slot.studentId?.name || "N/A"}</td>
                    <td>{slot.studentId?.email || "N/A"}</td>
                    <td>
                      <span
                        style={{
                          color: expired ? "#ef4444" : "#22c55e",
                          fontWeight: 500,
                          fontSize: "13px",
                          textTransform: "capitalize",
                        }}
                      >
                        {slot.status}
                      </span>
                    </td>
                    <td>
                      {expired || slot.status === "completed" ? (
                        <button className="lesson-btn expired" disabled>
                          Completed
                        </button>
                      ) : (
                        <button
                          className="lesson-btn"
                          onClick={() => openSlotModal(slot)}
                        >
                          Join
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Mobile Cards */}
          <div className="cards-container">
            {bookedSlots.map((slot, idx) => {
              const expired = isSlotExpired(slot);
              return (
                <div className="slot-card" key={slot._id || idx}>
                  <p>
                    <strong>#:</strong> {idx + 1}
                  </p>
                  <p>
                    <strong>Date:</strong> {formatBookingDate(slot.date)}
                  </p>{" "}
                  {/* ← Fixed Here */}
                  <p>
                    <strong>Time:</strong>{" "}
                    {formatSlotTime(slot.time, slot.duration)}
                  </p>
                  <p>
                    <strong>Duration:</strong>{" "}
                    {slot.duration ? `${slot.duration} min` : "N/A"}
                  </p>
                  <p>
                    <strong>Student:</strong> {slot.studentId?.name || "N/A"}
                  </p>
                  <p>
                    <strong>Email:</strong> {slot.studentId?.email || "N/A"}
                  </p>
                  <p>
                    <strong>Status: </strong>
                    <span style={{ color: expired ? "#ef4444" : "#22c55e" }}>
                      {expired ? "Completed" : "Upcoming"}
                    </span>
                  </p>
                  <button
                    className={`lesson-btn ${expired ? "expired" : ""}`}
                    disabled={expired}
                    onClick={() => !expired && openSlotModal(slot)}
                  >
                    {expired ? "Completed" : "Join"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "3rem 2rem",
            background: "#111",
            borderRadius: "12px",
            border: "1px solid #333",
            marginTop: "1rem",
          }}
        >
          <p style={{ fontSize: "2rem", marginBottom: "1rem" }}>📅</p>
          <p style={{ color: "#888", marginBottom: "1rem" }}>
            No bookings found yet.
          </p>
          <p style={{ color: "#555", fontSize: "13px" }}>
            Students will appear here once they book a session.
          </p>
        </div>
      )}

      {/* Modal */}
      {modalOpen && currentSlot && (
        <div className="video-modal-overlay" onClick={closeModal}>
          <div
            className="video-modal-content responsive-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <h3 style={{ margin: 0, color: "#000" }}>Session Details</h3>
              <IoCloseOutline
                size={28}
                style={{ cursor: "pointer", color: "#000" }}
                onClick={closeModal}
              />
            </div>

            <div style={{ padding: "0.5rem 0 1rem" }}>
              <p>
                <strong>Student:</strong> {currentSlot.studentId?.name || "N/A"}
              </p>
              <p>
                <strong>Date:</strong> {formatBookingDate(currentSlot.date)}
              </p>
              <p>
                <strong>Time:</strong>{" "}
                {formatSlotTime(currentSlot.time, currentSlot.duration)}
              </p>
              <p>
                <strong>Duration:</strong>{" "}
                {currentSlot.duration ? `${currentSlot.duration} min` : "N/A"}
              </p>
              <p>
                <strong>Email:</strong> {currentSlot.studentId?.email || "N/A"}
              </p>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {currentSlot.zoomStartUrl ? (
                <a
                  href={currentSlot.zoomStartUrl}
                  target="_blank"
                  onClick={() => updateStatus(currentSlot)}
                  rel="noreferrer"
                  style={{
                    display: "block",
                    textAlign: "center",
                    padding: "12px",
                    borderRadius: "8px",
                    background: "#2563eb",
                    color: "#fff",
                    fontWeight: 600,
                    textDecoration: "none",
                  }}
                >
                  🎥 Join Zoom Meeting
                </a>
              ) : (
                <div
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "#1a1a1a",
                    border: "1px solid #333",
                    color: "#888",
                    textAlign: "center",
                  }}
                >
                  Zoom link not available yet
                </div>
              )}

              <button
                onClick={closeModal}
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  background: "#333",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Bookings;
